  import React, { useState, useEffect } from 'react';
import { Container, Nav, Tab, Badge, Card, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBrain, 
  faUsers, 
  faChartLine, 
  faDollarSign, 
  faBuilding, 
  faCog,
  faBolt,
  faRocket,
  faDownload,
  faFolderOpen,
  faFileContract
} from '@fortawesome/free-solid-svg-icons';
import KairosLogo from 'assets/img/logos/KairosIQ-LogoOnly.png';
import BrokerDashboard from './BrokerDashboard';
import BrokerDashboardEnhanced from './BrokerDashboardEnhanced';
import AgentDirectory from './AgentDirectory';
// import BrokerAnalytics from './BrokerAnalytics'; // Temporarily disabled for go-live
import BrokerBilling from './BrokerBilling';
import BrokerAgentImport from './BrokerAgentImport';
// import BrokerMLSOnboarding from './BrokerMLSOnboarding'; // Temporarily disabled for go-live
import BrokerSettings from './BrokerSettings';
import BrokerDocumentManager from './BrokerDocumentManager';
import AIDocumentReview from './AIDocumentReview';
import CommissionPlansSection from './CommissionPlans/CommissionPlansSection';
import BrokerApiService from '../../services/brokerApiService';
import featureFlagService from '../../services/featureFlagService';
import axiosInstance from '../../helpers/api';
import './BrokerIntelligenceHub.css';

interface BrokerIntelligenceHubProps {
  brokerageId?: string;
  className?: string;
}

interface BrokerageData {
  brokerage_id: string;
  brokerage_name: string;
  agent_count: number;
  monthly_revenue: number;
  plan_type: string;
}

const BrokerIntelligenceHub: React.FC<BrokerIntelligenceHubProps> = ({ 
  brokerageId, 
  className 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract tab from URL path
  const getTabFromPath = () => {
    const pathParts = location.pathname.split('/');
    const tabIndex = pathParts.indexOf('broker-intelligence') + 1;
    const validTabs = ['dashboard', 'agents', 'billing', 'commission-plans', 'documents', 'ai-review', 'import', 'settings'];
    const urlTab = pathParts[tabIndex];
    
    return validTabs.includes(urlTab) ? urlTab : 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [brokerageData, setBrokerageData] = useState<BrokerageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actualBrokerageId, setActualBrokerageId] = useState<string | undefined>(undefined);

  // Update tab when URL changes (browser back/forward)
  useEffect(() => {
    const newTab = getTabFromPath();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchBrokerageData = async () => {
      let currentBrokerageId: string = '';
      let currentBrokerageName: string = '';
      
      try {
        // First, try to get company profile from API
        console.log('🔍 Fetching company profile from API...');
        
        const profileResponse = await axiosInstance.get('/broker/company-profile');

        if (profileResponse.data.success && profileResponse.data.data) {
          currentBrokerageId = profileResponse.data.data.brokerage_id;
          currentBrokerageName = profileResponse.data.data.company_name;

          // Cache in localStorage
          localStorage.setItem('brokerageId', currentBrokerageId);
          localStorage.setItem('brokerageName', currentBrokerageName);

          console.log('✅ Retrieved company profile:', {
            id: currentBrokerageId,
            name: currentBrokerageName
          });

          // Set brokerage data immediately
          setBrokerageData({
            brokerage_id: currentBrokerageId,
            brokerage_name: currentBrokerageName,
            agent_count: 0,
            monthly_revenue: 0,
            plan_type: 'team'
          });
        }
        
        // If no brokerage ID from API, check props then localStorage
        if (!currentBrokerageId && brokerageId) {
          currentBrokerageId = brokerageId;
        }
        
        if (!currentBrokerageId) {
          currentBrokerageId = localStorage.getItem('brokerageId') || '';
          currentBrokerageName = localStorage.getItem('brokerageName') || '';
          console.log('🔍 [DEBUG] Using localStorage values:', {
            id: currentBrokerageId,
            name: currentBrokerageName
          });
        }
        
        // Ensure currentBrokerageId is not null/undefined/empty before proceeding
        if (!currentBrokerageId || currentBrokerageId === 'undefined' || currentBrokerageId === 'null') {
          console.warn('⚠️ Unable to determine brokerage ID');
          currentBrokerageId = '';
        }
        
        setActualBrokerageId(currentBrokerageId);
        
        console.log('🏢 Fetching brokerage data for:', currentBrokerageId);
        
        // If we have a brokerage ID, try to get the name from localStorage first
        if (currentBrokerageId) {
          const cachedBrokerageName = localStorage.getItem('brokerageName');
          if (cachedBrokerageName) {
            setBrokerageData({
              brokerage_id: currentBrokerageId,
              brokerage_name: cachedBrokerageName,
              agent_count: 0,
              monthly_revenue: 0,
              plan_type: 'team'
            });
          }
        }
        
        const response = await axiosInstance.get(`/broker/billing/${currentBrokerageId}`);
        const data = response.data;
        
        if (data.success) {
          setBrokerageData({
            brokerage_id: currentBrokerageId,
            brokerage_name: data.billing.brokerage_name,
            agent_count: data.billing.current_month.agent_counts.total,
            monthly_revenue: data.billing.current_month.total_amount,
            plan_type: data.billing.plan_type
          });
        } else {
          console.error('Failed to fetch brokerage data:', data.error);
        }
      } catch (error) {
        console.error('Error fetching brokerage data:', error);
        
        // If we have a brokerage ID but billing failed, still show the brokerage
        // Use currentBrokerageId instead of actualBrokerageId (which might not be set yet)
        if (currentBrokerageId) {
          const cachedBrokerageName = localStorage.getItem('brokerageName');
          setBrokerageData({
            brokerage_id: currentBrokerageId,
            brokerage_name: cachedBrokerageName || 'Your Brokerage',
            agent_count: 0,
            monthly_revenue: 0,
            plan_type: 'team'
          });
        } else {
          // Only show "No Brokerage Set" if there's truly no brokerage
          setBrokerageData({
            brokerage_id: '',
            brokerage_name: 'No Brokerage Set',
            agent_count: 0,
            monthly_revenue: 0,
            plan_type: 'none'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBrokerageData();
  }, [brokerageId]);

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: faChartLine, badge: null },
    { key: 'agents', label: 'Agent Directory', icon: faUsers, badge: null },
    // { key: 'analytics', label: 'Analytics', icon: faBrain, badge: null }, // Temporarily disabled for go-live
    { key: 'billing', label: 'Billing', icon: faDollarSign, badge: null },
    { key: 'commission-plans', label: 'Commission Plans', icon: faFileContract, badge: null },
    { key: 'documents', label: 'Documents', icon: faFolderOpen, badge: null },
    { key: 'ai-review', label: 'AI Review', icon: 'kairos-logo' as any, badge: null },
    // { key: 'onboarding', label: 'MLS Onboarding', icon: faRocket, badge: null }, // Temporarily disabled for go-live
    { key: 'import', label: 'Import Agents', icon: faDownload, badge: null },
    { key: 'settings', label: 'Settings', icon: faCog, badge: null }
  ];

  // Debug function to check localStorage
  const debugLocalStorage = () => {
    const id = localStorage.getItem('brokerageId');
    const name = localStorage.getItem('brokerageName');
    console.log('🔍 DEBUG - LocalStorage Check:');
    console.log('  brokerageId:', id);
    console.log('  brokerageName:', name);
    console.log('  actualBrokerageId state:', actualBrokerageId);
    console.log('  brokerageData state:', brokerageData);
  };

  // Show loading state while fetching brokerage info
  if (loading && !actualBrokerageId) {
    return (
      <Container fluid className={`broker-intelligence-hub ${className || ''}`}>
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: '#00d4ff' }} />
          <p className="mt-3 text-muted">Loading brokerage information...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className={`broker-intelligence-hub ${className || ''}`}>
      {/* AI Intelligence Header */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          borderRadius: '16px',
          padding: '32px',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          marginBottom: '2rem'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-4"
              style={{ 
                width: "64px", 
                height: "64px",
                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)'
              }}
            >
              <FontAwesomeIcon 
                icon={faBrain} 
                className="text-white" 
                size="2x"
                style={{ filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.5))' }}
              />
            </div>
            <div>
              <h1 style={{ 
                color: '#1f2937',
                textShadow: '0 0 15px rgba(0, 212, 255, 0.3)',
                fontSize: '2.2rem',
                fontWeight: 600,
                marginBottom: '8px'
              }}>
                Broker Intelligence Hub
              </h1>
              <p className="text-muted mb-0" style={{ fontSize: '16px' }}>
                AI-powered brokerage management and analytics platform
              </p>
              {brokerageData && (
                <div className="mt-2">
                  <div className="d-flex align-items-center mb-3">
                    <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
                    <h4 className="mb-0 fw-semibold text-primary">
                      {brokerageData ? brokerageData.brokerage_name : 'Your Brokerage'}
                    </h4>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <Badge 
                      style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        textTransform: 'uppercase'
                      }}
                    >
                      <FontAwesomeIcon icon={faBuilding} className="me-1" />
                      {brokerageData.brokerage_name}
                    </Badge>
                    <Badge 
                      style={{ 
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        fontSize: '12px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        textTransform: 'uppercase'
                      }}
                    >
                      <FontAwesomeIcon icon={faRocket} className="me-1" />
                      {brokerageData.plan_type.toUpperCase()} PLAN
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* AI Status Indicator */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            padding: '12px 20px',
            borderRadius: '25px',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <div className="d-flex align-items-center">
              <div className="rounded-circle me-2" style={{
                width: '10px', 
                height: '10px', 
                background: '#10b981',
                animation: 'pulse 2s infinite'
              }} />
              <span className="fw-semibold text-success" style={{ 
                fontSize: '14px', 
                textTransform: 'uppercase', 
                letterSpacing: '0.5px'
              }}>
                <FontAwesomeIcon icon={faBolt} className="me-1" />
                AI INTELLIGENCE ACTIVE
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <Tab.Container 
        activeKey={activeTab} 
        onSelect={(key) => {
          const newTab = key || 'dashboard';
          setActiveTab(newTab);
          
          // Update URL to reflect the selected tab
          const basePath = '/broker-intelligence';
          const newPath = newTab === 'dashboard' ? basePath : `${basePath}/${newTab}`;
          navigate(newPath);
        }}
      >
        <Card 
          className="border-0 mb-4"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(0, 212, 255, 0.2) !important'
          }}
        >
          <Card.Header 
            className="border-bottom-0 p-0"
            style={{ 
              background: 'transparent',
              borderRadius: '16px 16px 0 0'
            }}
          >
            <Nav variant="tabs" className="ai-tabs border-0">
              {tabs.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link 
                    eventKey={tab.key}
                    className="d-flex align-items-center px-4 py-3"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      borderRadius: '0',
                      borderBottom: '3px solid transparent',
                      transition: 'all 0.3s ease',
                      color: activeTab === tab.key ? '#00d4ff' : '#6b7280'
                    }}
                  >
                    {tab.icon === 'kairos-logo' ? (
                      <img 
                        src={KairosLogo} 
                        alt="Kairos" 
                        className="me-2"
                        style={{ 
                          width: '20px', 
                          height: '20px',
                          opacity: activeTab === tab.key ? 1 : 0.6,
                          filter: activeTab === tab.key ? 'brightness(1.2)' : 'none'
                        }}
                      />
                    ) : (
                      <FontAwesomeIcon 
                        icon={tab.icon} 
                        className="me-2" 
                        style={{
                          color: activeTab === tab.key ? '#00d4ff' : '#6b7280'
                        }}
                      />
                    )}
                    <span className="fw-semibold">{tab.label}</span>
                    {tab.badge && (
                      <Badge 
                        bg={activeTab === tab.key ? 'primary' : 'secondary'} 
                        className="ms-2"
                        style={{ fontSize: '11px' }}
                      >
                        {tab.badge}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Header>

          <Card.Body className="p-0" style={{ background: 'transparent' }}>
            <Tab.Content>
              <Tab.Pane eventKey="dashboard">
                <div className="p-4">
                  {featureFlagService.isEnabled('brokerDashboardV2') ? (
                    <BrokerDashboardEnhanced 
                      brokerageId={actualBrokerageId} 
                      loading={loading}
                    />
                  ) : (
                    <BrokerDashboard 
                      brokerageId={actualBrokerageId} 
                      data={brokerageData}
                      loading={loading}
                    />
                  )}
                </div>
              </Tab.Pane>
              
              <Tab.Pane eventKey="agents">
                <div className="p-4">
                  <AgentDirectory 
                    brokerageId={actualBrokerageId}
                    totalAgents={brokerageData?.agent_count}
                  />
                </div>
              </Tab.Pane>
              
              {/* Temporarily disabled for go-live */}
              {/* <Tab.Pane eventKey="analytics">
                <div className="p-4">
                  <BrokerAnalytics 
                    brokerageId={actualBrokerageId}
                    data={brokerageData}
                  />
                </div>
              </Tab.Pane> */}
              
              <Tab.Pane eventKey="billing">
                <div className="p-4">
                  <BrokerBilling 
                    brokerageId={actualBrokerageId}
                    data={brokerageData}
                  />
                </div>
              </Tab.Pane>
              
              <Tab.Pane eventKey="commission-plans">
                <div className="p-4">
                  <CommissionPlansSection />
                </div>
              </Tab.Pane>
              
              <Tab.Pane eventKey="documents">
                <div className="p-4">
                  <BrokerDocumentManager 
                    brokerageId={actualBrokerageId}
                    brokerageName={brokerageData?.brokerage_name}
                  />
                </div>
              </Tab.Pane>
              
              <Tab.Pane eventKey="ai-review">
                <div className="p-4">
                  <AIDocumentReview 
                    brokerageId={actualBrokerageId}
                    brokerageName={brokerageData?.brokerage_name}
                  />
                </div>
              </Tab.Pane>
              
              {/* Temporarily disabled for go-live */}
              {/* <Tab.Pane eventKey="onboarding">
                <div className="p-4">
                  <BrokerMLSOnboarding />
                </div>
              </Tab.Pane> */}
              
              <Tab.Pane eventKey="import">
                <div className="p-4">
                  <BrokerAgentImport />
                </div>
              </Tab.Pane>
              
              <Tab.Pane eventKey="settings">
                <div className="p-4">
                  <BrokerSettings 
                    brokerageId={actualBrokerageId}
                    data={brokerageData}
                  />
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
    </Container>
  );
};

export default BrokerIntelligenceHub;